import { useCallback, useRef, useState } from 'react'
import { apiGet, apiPut } from '../../../generic/Api_Functions'
import { JOB_ACTION_INTENT, resolveTargetStatusCode } from '../FabricationOrderJobReference/PlannedJobs/utils/jobStatus'

/**
 * Story 113 (S118/S123/S36, RN42) — the one-step mode transition, shared by the four station widgets.
 *
 * RN42: going from preparation to execution (and back) is ONE STEP and does NOT go through the
 * change-status dialog -- which would additionally have titled it "Start", because its title used to
 * be derived from the code containing "ACTIVE". So the request is sent straight from the button, over
 * the endpoints that already exist: the status id is resolved by
 * `GET fabricationOrderStatus/byCode/{code}` and the change sent by
 * `PUT mos/deviceFabricationOrder/updateStatus`. No new route (section 8.0/8.2).
 *
 * S36 (enmienda 02, a rule of the INTERFACE, not of the server) — the job may have been paused,
 * finished or cancelled from another station between the render and the click. Sun CANNOT reject
 * that: a transition and a start reach it through the same gate with the same shape (only `statusId`
 * differs), and for "active target with no open row on this machine" S25/S26 already require it to
 * OPEN a period. It would therefore silently start the job again. So the guarantee lives here: if the
 * job is no longer in this machine's active context, the station refreshes and tells the operator
 * instead of sending anything. The message reuses an EXISTING key -- enmienda 02 forbids a new error
 * literal and a new i18n key alike.
 *
 * Because the transition bypasses the change-status dialog (RN42), it also bypassed the dialog's
 * error surface: a rejection from Sun (e.g. a worker without permission to execute the job) escaped
 * as an unhandled promise rejection and the operator saw nothing happen. The rejection is presented
 * here with the very same shape the dialog uses (`showStatusChangeError`), so both paths report a
 * refused change identically.
 */
const useJobModeTransition = ({
  device,
  fabricationOrder,
  jobReference,
  worker,
  activeJobs,
  refreshActiveJobs,
  showAlert,
  t_
}) => {
  const inFlightRef = useRef(false)
  const [busy, setBusy] = useState(false)

  // Same presentation as the change-status dialog's `showStatusChangeError`: Sun answers either a
  // single message or a list of them, and the alert carries it as the title (AlertUI renders the
  // title in bold and the message after it).
  const showTransitionError = useCallback((error) => {
    console.error('Error changing job mode:', error)
    const errorMessage = Array.isArray(error?.data)
      ? error.data.map((message) => t_(message)).join('. ')
      : t_(error?.message || 'Error updating status')
    if (showAlert) showAlert({ severity: 'error', title: errorMessage })
  }, [t_, showAlert])

  const requestModeChange = useCallback(async (intent) => {
    // The transition sends `apiGet` + `apiPut` straight from the button, with no loading state and
    // nothing disabled, so every click was a request: two clicks, two transitions. Marked
    // SYNCHRONOUSLY on the way in -- a state flag would be read stale by the second click of the
    // same event batch, which is precisely how the duplicate start reached production.
    if (inFlightRef.current) return
    inFlightRef.current = true
    setBusy(true)
    try {
      const list = Array.isArray(activeJobs) ? activeJobs : []
      const stillInProgress = list.some((item) =>
        item?.fabricationOrder?.id === fabricationOrder?.id &&
        item?.jobReference?.id === jobReference?.id)

      // S36: refresh and warn, send nothing. The refresh also drops the job from the widget, so the
      // buttons become the ones of its real state.
      if (!stillInProgress) {
        if (refreshActiveJobs) refreshActiveJobs()
        if (showAlert) showAlert({ severity: 'warning', message: t_('There is no active job') })
        return
      }

      const statusCode = resolveTargetStatusCode(intent)
      try {
        const status = await apiGet('fabricationOrderStatus/byCode/' + statusCode)
        await apiPut('mos/deviceFabricationOrder/updateStatus', {
          deviceId: device?.id,
          fabricationOrderId: fabricationOrder?.id,
          statusId: status?.id,
          comment: '',
          workerId: worker?.id,
          jobReferenceId: jobReference?.id,
          reason: null,
          check: true,
          autoReport: false,
          confirmReportIncompleteAsNok: false
        })
      } catch (error) {
        // Nothing changed on the server, so the widget is left exactly as it was -- only the operator
        // is told why the mode did not change.
        showTransitionError(error)
        return
      }
      if (refreshActiveJobs) refreshActiveJobs()
    } finally {
      inFlightRef.current = false
      setBusy(false)
    }
  }, [activeJobs, device, fabricationOrder, jobReference, worker, refreshActiveJobs, showAlert, showTransitionError, t_])

  return {
    goToExecution: useCallback(
      () => requestModeChange(JOB_ACTION_INTENT.GO_TO_EXECUTION), [requestModeChange]),
    backToPreparation: useCallback(
      () => requestModeChange(JOB_ACTION_INTENT.BACK_TO_PREPARATION), [requestModeChange]),
    // For the four station widgets to disable the mode button while a transition is in flight: the
    // ref alone stops the duplicate request, this is what stops the button looking alive.
    busy
  }
}

export default useJobModeTransition
